import { IAmendmentResponse } from '../../../../../model/sge/amendment/response/amendment-response';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { ILiquidationResponse } from '../../../../../model/sge/liquidation/response/liquidation-response';
import { IPaymentResponse } from '../../../../../model/sge/payment/response/payment-response';
import { ITransferResponse } from '../../../../../model/sge/transfer/response/transfer-response';
import { IAccountReportResponse } from '../../../../../model/sge/account-report/response/account-report-response';
import { IAttachmentResponse } from '../../../../../model/sge/attachment/response/attachment-response';
import { ICommitmentAddEditRequest } from '../../../../../model/sge/commitment/request/commitment-add-edit-request';
import { ILiquidationAddEditRequest } from '../../../../../model/sge/liquidation/request/liquidation-add-edit-request';
import { IPaymentAddEditRequest } from '../../../../../model/sge/payment/request/payment-add-edit-request';
import { ITransferAddEditRequest } from '../../../../../model/sge/transfer/request/transfer-add-edit-request';
import { IAccountReportAddEditRequest } from '../../../../../model/sge/account-report/request/account-report-add-edit-request';
import { IParlamentarianPagedResponse } from '../../../../../model/sge/parlamentarian/response/parlamentarian-paged-response';
import { IPoliticalPartyPagedResponse } from '../../../../../model/sge/political-party/response/political-party-paged-response';
import { ISupplierPagedResponse } from '../../../../../model/sge/supplier/response/supplier-paged-response';

export type AmendmentDetailState = {
    amendmentId: string;
    amendment?: IAmendmentResponse;
    parlamentarians: IParlamentarianPagedResponse[];
    politicalParties: IPoliticalPartyPagedResponse[];
    suppliers: ISupplierPagedResponse[];
    tabIndex: number;
    commitments: ICommitmentResponse[];
    selectedCommitment?: ICommitmentResponse;
    liquidations: ILiquidationResponse[];
    payments: IPaymentResponse[];
    transfers: ITransferResponse[];
    accountReports: IAccountReportResponse[];
    attachments: IAttachmentResponse[];
    commitmentForm?: Partial<ICommitmentAddEditRequest>;
    liquidationForm?: Partial<ILiquidationAddEditRequest>;
    paymentForm?: Partial<IPaymentAddEditRequest>;
    transferForm?: Partial<ITransferAddEditRequest>;
    accountReportForm?: Partial<IAccountReportAddEditRequest>;
    accountReportFiles: File[];
    accountReportAttachments: IAttachmentResponse[];
    showCommitmentDialog: boolean;
    showCommitmentValidationErrors: boolean;
    showLiquidationDialog: boolean;
    showLiquidationValidationErrors: boolean;
    showPaymentDialog: boolean;
    showTransferDialog: boolean;
    showTransferValidationErrors: boolean;
    showAccountReportDialog: boolean;
};

export enum ACTION_TYPES {
    setAmendment = 'setAmendment',
    setParlamentarians = 'setParlamentarians',
    setPoliticalParties = 'setPoliticalParties',
    setSuppliers = 'setSuppliers',
    setTabIndex = 'setTabIndex',
    setCommitments = 'setCommitments',
    setSelectedCommitment = 'setSelectedCommitment',
    setLiquidations = 'setLiquidations',
    setPayments = 'setPayments',
    setTransfers = 'setTransfers',
    setAccountReports = 'setAccountReports',
    setAttachments = 'setAttachments',
    setCommitmentForm = 'setCommitmentForm',
    updateCommitmentForm = 'updateCommitmentForm',
    setLiquidationForm = 'setLiquidationForm',
    updateLiquidationForm = 'updateLiquidationForm',
    setPaymentForm = 'setPaymentForm',
    updatePaymentForm = 'updatePaymentForm',
    setTransferForm = 'setTransferForm',
    updateTransferForm = 'updateTransferForm',
    setAccountReportForm = 'setAccountReportForm',
    updateAccountReportForm = 'updateAccountReportForm',
    setAccountReportFiles = 'setAccountReportFiles',
    setAccountReportAttachments = 'setAccountReportAttachments',
    setShowCommitmentDialog = 'setShowCommitmentDialog',
    setShowCommitmentValidationErrors = 'setShowCommitmentValidationErrors',
    setShowLiquidationDialog = 'setShowLiquidationDialog',
    setShowLiquidationValidationErrors = 'setShowLiquidationValidationErrors',
    setShowPaymentDialog = 'setShowPaymentDialog',
    setShowTransferDialog = 'setShowTransferDialog',
    setShowTransferValidationErrors = 'setShowTransferValidationErrors',
    setShowAccountReportDialog = 'setShowAccountReportDialog',
}

export const AmendmentDetailReducer = (state: AmendmentDetailState, action: any): AmendmentDetailState => {
    const { type, payload } = action;
    switch (type) {
        case ACTION_TYPES.setAmendment: return { ...state, amendment: payload };
        case ACTION_TYPES.setParlamentarians: return { ...state, parlamentarians: payload };
        case ACTION_TYPES.setPoliticalParties: return { ...state, politicalParties: payload };
        case ACTION_TYPES.setSuppliers: return { ...state, suppliers: payload };
        case ACTION_TYPES.setTabIndex: return { ...state, tabIndex: payload };
        case ACTION_TYPES.setCommitments: return { ...state, commitments: payload };
        case ACTION_TYPES.setSelectedCommitment: return { ...state, selectedCommitment: payload, liquidations: [], payments: [] };
        case ACTION_TYPES.setLiquidations: return { ...state, liquidations: payload };
        case ACTION_TYPES.setPayments: return { ...state, payments: payload };
        case ACTION_TYPES.setTransfers: return { ...state, transfers: payload };
        case ACTION_TYPES.setAccountReports: return { ...state, accountReports: payload };
        case ACTION_TYPES.setAttachments: return { ...state, attachments: payload };
        case ACTION_TYPES.setCommitmentForm: return { ...state, commitmentForm: payload };
        case ACTION_TYPES.updateCommitmentForm: return { ...state, commitmentForm: { ...state.commitmentForm, ...payload } };
        case ACTION_TYPES.setLiquidationForm: return { ...state, liquidationForm: payload };
        case ACTION_TYPES.updateLiquidationForm: return { ...state, liquidationForm: { ...state.liquidationForm, ...payload } };
        case ACTION_TYPES.setPaymentForm: return { ...state, paymentForm: payload };
        case ACTION_TYPES.updatePaymentForm: return { ...state, paymentForm: { ...state.paymentForm, ...payload } };
        case ACTION_TYPES.setTransferForm: return { ...state, transferForm: payload };
        case ACTION_TYPES.updateTransferForm: return { ...state, transferForm: { ...state.transferForm, ...payload } };
        case ACTION_TYPES.setAccountReportForm: return { ...state, accountReportForm: payload };
        case ACTION_TYPES.updateAccountReportForm: return { ...state, accountReportForm: { ...state.accountReportForm, ...payload } };
        case ACTION_TYPES.setAccountReportFiles: return { ...state, accountReportFiles: payload };
        case ACTION_TYPES.setAccountReportAttachments: return { ...state, accountReportAttachments: payload };
        case ACTION_TYPES.setShowCommitmentDialog: return { ...state, showCommitmentDialog: payload };
        case ACTION_TYPES.setShowCommitmentValidationErrors: return { ...state, showCommitmentValidationErrors: payload };
        case ACTION_TYPES.setShowLiquidationDialog: return { ...state, showLiquidationDialog: payload };
        case ACTION_TYPES.setShowLiquidationValidationErrors: return { ...state, showLiquidationValidationErrors: payload };
        case ACTION_TYPES.setShowPaymentDialog: return { ...state, showPaymentDialog: payload };
        case ACTION_TYPES.setShowTransferDialog: return { ...state, showTransferDialog: payload };
        case ACTION_TYPES.setShowTransferValidationErrors: return { ...state, showTransferValidationErrors: payload };
        case ACTION_TYPES.setShowAccountReportDialog: return { ...state, showAccountReportDialog: payload };
        default: throw new Error(`Unhandled action type: ${type}`);
    }
};
